import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { canUseFavorites } from '@/constants/role-policy';
import { buildLoginRedirectHref } from '@/lib/auth-redirect';
import { useAuthStore } from '@/stores/auth-store';
import { useState } from 'react';
import { ItemDetailDrawer } from '@/components/items/ItemDetailDrawer';
import { ItemGrid } from '@/components/items/ItemGrid';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useFavorites } from '@/hooks/useFavorites';
import { myPageRoute } from './route';

export const Route = createRoute({
  getParentRoute: () => myPageRoute,
  path: 'favorites',
  beforeLoad: ({ location }) => {
    const { user } = useAuthStore.getState();

    if (!canUseFavorites(user?.roles)) {
      throw redirect({ href: buildLoginRedirectHref(location.href) });
    }
  },
  component: FavoritesPage,
});

function FavoritesPage() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { data, isLoading, isError, error } = useFavorites();
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);

  const items = data?.items ?? [];
  const favoriteCount = data?.count ?? items.length;
  return (
    <div className="space-y-6" data-ai-id="my-page-favorites-page">
      <div className="space-y-2" data-ai-id="my-page-favorites-header">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500" data-ai-id="my-page-favorites-kicker">
          Saved
        </p>
        <h2 className="text-3xl font-semibold tracking-tight text-slate-900" data-ai-id="my-page-favorites-title">
          Saved items{favoriteCount > 0 ? ` (${favoriteCount})` : ''}
        </h2>
        <p className="text-sm text-slate-600" data-ai-id="my-page-favorites-description">
          Keep your favorite components and sections in one place. Revisit your newest saves anytime.
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-ai-id="my-page-favorites-loading-state">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4" data-ai-id="my-page-favorites-loading-card">
              <Skeleton className="aspect-[4/3] w-full rounded-xl" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-full" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700" data-ai-id="my-page-favorites-error-state">
          {error instanceof Error ? error.message : "Couldn't load saved items."}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-ai-id="my-page-favorites-empty-state">
          <p data-ai-id="my-page-favorites-empty-message">You haven't saved any items yet.</p>
          <Button
            variant="secondary"
            className="mt-4"
            onClick={() => void navigate({ to: '/' })}
            data-ai-id="my-page-favorites-empty-home-button"
          >
            Browse library
          </Button>
        </div>
      ) : (
        <div className="space-y-5" data-ai-id="my-page-favorites-results">
          <ItemGrid
            items={items}
            onItemClick={(item) => {
              if (isDesktop) {
                setSelectedItemId(item.id);
                return;
              }

              void navigate({
                to: '/items/$itemId',
                params: { itemId: String(item.id) },
              });
            }}
          />

          <div data-ai-id="my-page-favorites-drawer">
            <ItemDetailDrawer
              open={isDesktop && selectedItemId !== null}
              itemId={selectedItemId}
              onClose={() => setSelectedItemId(null)}
            />
          </div>
        </div>
      )}

    </div>
  );
}
